import * as THREE from "three";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

/** The UnrealBloomPass internals this subclass drives (they are public in the JS, untyped in the .d.ts). */
interface BloomInternals {
  fsQuad: { material: THREE.Material; render(r: THREE.WebGLRenderer): void };
  highPassUniforms: Record<string, THREE.IUniform>;
  materialHighPassFilter: THREE.Material;
  separableBlurMaterials: THREE.ShaderMaterial[];
  renderTargetBright: THREE.WebGLRenderTarget;
  renderTargetsHorizontal: THREE.WebGLRenderTarget[];
  renderTargetsVertical: THREE.WebGLRenderTarget[];
  compositeMaterial: THREE.ShaderMaterial;
  bloomTintColors: THREE.Vector3[];
  nMips: number;
}

const BlurX = new THREE.Vector2(1, 0), BlurY = new THREE.Vector2(0, 1);

/**
 * UnrealBloomPass without its last step, and at a chosen fraction of the canvas.
 *
 * The stock pass ends by drawing its result additively over the full-resolution scene target: a full-screen pass,
 * and with an MSAA scene target a second full-screen resolve after it. Here the output pass samples `texture` and adds
 * it itself, in the one full-screen pass the frame already pays for. (The stock blend used SRC_ALPHA, ONE on a
 * half-float target, where the factor is not clamped: the added light is rgb * a, and the output pass does the same.)
 *
 * The stock pass also ignores its `resolution` after construction — every resize ran it at half the canvas whatever
 * the tier asked for. `scale` is honoured here.
 *
 * It also has a soft knee: the stock pass hard-codes `smoothWidth` 0.01, so anything a hair over the threshold blooms
 * at full weight. `knee` is the width of the ramp above the threshold (lighting review F2: threshold 1.1, knee ~0.15).
 */
export class BloomMips extends UnrealBloomPass {
  scale = 1;
  knee = 0.01;

  constructor(strength: number, radius: number, threshold: number) {
    super(new THREE.Vector2(1, 1), strength, radius, threshold);
    this.needsSwap = false;
  }

  /** The composited bloom, valid after `render`: add rgb * a over the scene. */
  get texture(): THREE.Texture { return (this as unknown as BloomInternals).renderTargetsHorizontal[0].texture; }

  override setSize(width: number, height: number): void {
    super.setSize(Math.max(2, Math.round(width * this.scale)), Math.max(2, Math.round(height * this.scale)));
  }

  override render(renderer: THREE.WebGLRenderer, _write: THREE.WebGLRenderTarget, readBuffer: THREE.WebGLRenderTarget): void {
    const b = this as unknown as BloomInternals;
    const oldClear = renderer.getClearColor(new THREE.Color()), oldAlpha = renderer.getClearAlpha(), oldAuto = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setClearColor(0x000000, 0);
    b.highPassUniforms.tDiffuse.value = readBuffer.texture;
    b.highPassUniforms.luminosityThreshold.value = this.threshold;
    b.highPassUniforms.smoothWidth.value = this.knee;
    b.fsQuad.material = b.materialHighPassFilter;
    renderer.setRenderTarget(b.renderTargetBright); renderer.clear(); b.fsQuad.render(renderer);
    let input = b.renderTargetBright;
    for (let i = 0; i < b.nMips; i++) {
      const m = b.separableBlurMaterials[i];
      b.fsQuad.material = m;
      m.uniforms.colorTexture.value = input.texture; m.uniforms.direction.value = BlurX;
      renderer.setRenderTarget(b.renderTargetsHorizontal[i]); renderer.clear(); b.fsQuad.render(renderer);
      m.uniforms.colorTexture.value = b.renderTargetsHorizontal[i].texture; m.uniforms.direction.value = BlurY;
      renderer.setRenderTarget(b.renderTargetsVertical[i]); renderer.clear(); b.fsQuad.render(renderer);
      input = b.renderTargetsVertical[i];
    }
    const c = b.compositeMaterial.uniforms;
    b.fsQuad.material = b.compositeMaterial;
    c.bloomStrength.value = this.strength; c.bloomRadius.value = this.radius; c.bloomTintColors.value = b.bloomTintColors;
    renderer.setRenderTarget(b.renderTargetsHorizontal[0]); renderer.clear(); b.fsQuad.render(renderer);
    renderer.setClearColor(oldClear, oldAlpha);
    renderer.autoClear = oldAuto;
  }
}
